import React from 'react';
import {
  CssBaseline,
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  BottomNavigation,
  Avatar,
  BottomNavigationAction
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/TrendingUp';
import TableViewIcon from '@mui/icons-material/TableView';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { logout } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import PersonIcon from '@mui/icons-material/Person';

function Layout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  
  // Centralized function to get current page index
  const getCurrentPageIndex = (pathname) => {
    switch (pathname) {
      case '/analytics': return 1;
      case '/report': return 2;
      case '/settings': return 3;
      default: return 0;
    }
  };

  const [value, setValue] = React.useState(() => getCurrentPageIndex(location.pathname));
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  // Update value when pathname changes
  React.useEffect(() => {
    setValue(getCurrentPageIndex(location.pathname));
  }, [location.pathname]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, index: 0, path: '/' },
    { text: 'Data Analytics', icon: <AnalyticsIcon />, index: 1, path: '/analytics' },
    { text: 'Data Report', icon: <TableViewIcon />, index: 2, path: '/report' },
    { text: 'Device Settings', icon: <SettingsIcon />, index: 3, path: '/settings' }
  ];

  // Common transition style
  const commonTransition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

  const handleUserMenuClick = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(180deg, #41ab5d 0%, #00b862 100%)' }}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, width: '100%', fontFamily: '"Montserrat", sans-serif' }}>
        <Box sx={{ display: 'flex', width: '100%', maxWidth: '100vw' }}>
          {/* Desktop Sidebar */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: '100px',
                display: 'flex',
                alignItems: 'center',
                color: '#666666',
                border: 'none',
                position: 'sticky',
                top: '8px',
                height: 'calc(100vh - 16px)',
                alignSelf: 'flex-start',
                margin: '8px',
                borderRadius: '16px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.05), 0 12px 24px rgba(0,0,0,0.05), inset 0 0 0 1px rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                background: 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,0.95) 100%)'
              },
            }}
            open
          >
            <List sx={{ 
              px: 1.5, 
              mt: 1,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'auto',
              fontFamily: 'inherit',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 100%)'
            }}>
              {menuItems.map((item) => (
                <ListItem
                  key={item.text}
                  component={Link}
                  to={item.path}
                  selected={value === item.index}
                  sx={{ 
                    cursor: 'pointer',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 1.5,
                    mb: 0.5,
                    borderRadius: '12px',
                    transition: commonTransition,
                    '&.Mui-selected': {
                      backgroundColor: '#f0f7ff',
                      color: '#41ab5d',
                      boxShadow: '0 4px 12px rgba(0,119,204,0.12), inset 0 0 0 1px rgba(0,119,204,0.05)',
                      transform: 'translateY(-1px)',
                      '& .MuiListItemIcon-root': {
                        color: '#41ab5d',
                        transform: 'scale(1.1)'
                      }
                    },
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: value === item.index ? '#41ab5d' : '#89919a',
                    minWidth: 'auto',
                    mb: 1,
                    transition: commonTransition,
                    fontSize: '1.5rem'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      m: 0,
                      '& .MuiTypography-root': { 
                        fontWeight: value === item.index ? 600 : 500,
                        fontSize: '0.75rem',
                        textAlign: 'center',
                        color: value === item.index ? '#41ab5d' : '#89919a',
                        transition: commonTransition,
                        letterSpacing: '0.02em'
                      } 
                    }} 
                  />
                </ListItem>
              ))}
              
              <Box sx={{ flexGrow: 1 }} />
              
              {/* User Info Section */}
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 2
              }}>
                <Avatar 
                  onClick={handleUserMenuClick}
                  sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: '#41ab5d',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: commonTransition,
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }}
                >
                  <PersonIcon sx={{ fontSize: '1.5rem' }} />
                </Avatar>
              </Box>
            </List>
          </Drawer>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: '100%',
              p: { xs: 2, sm: 3 },
              ml: { xs: 0, md: 1 },
              height: { xs: 'calc(100vh - 86px)', md: 'calc(100vh - 16px)' },
              margin: { xs: '8px 8px 78px 8px', md: '8px' },
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: commonTransition,
            }}
          >
            <Box sx={{ 
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              width: '100%',
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              scrollPaddingTop: '20px',
            }}>
              {children}
            </Box>
          </Box>

          {/* Mobile Bottom Navigation */}
          <BottomNavigation
            value={value}
            onChange={(event, newValue) => setValue(newValue)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: '70px',
              bgcolor: '#ffffff',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              zIndex: 1200,
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '4px 8px',
                color: '#89919a',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                '&.Mui-selected': {
                  color: '#41ab5d',
                },
                '&:hover': {
                  color: '#41ab5d',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '22px !important',
                  width: '22px !important',
                  height: '22px !important',
                  display: 'block',
                  margin: '0 auto',
                  transform: 'translateZ(0)',
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem !important',
                  fontWeight: 500,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  marginTop: '2px !important',
                  '&.Mui-selected': {
                    fontSize: '0.7rem !important',
                    fontWeight: 600,
                  }
                }
              }
            }}
          >
            {menuItems.map((item) => (
              <BottomNavigationAction
                key={item.text}
                label={item.text}
                icon={item.icon}
                component={Link}
                to={item.path}
              />
            ))}
            <BottomNavigationAction
              label="Profile"
              icon={<PersonIcon />}
              onClick={handleUserMenuClick}
              sx={{
                '&:hover': {
                  color: '#41ab5d',
                }
              }}
            />
          </BottomNavigation>
        </Box>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          sx: {
            ml: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            minWidth: '200px'
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'center' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography 
            sx={{ 
              fontWeight: 600,
              color: '#1a1a1a',
              fontSize: '0.9rem',
              mb: 0.5
            }}
          >
            {user?.username || 'User'}
          </Typography>
          <Typography 
            sx={{ 
              color: '#666666',
              fontSize: '0.75rem'
            }}
          >
            {user?.email || ''}
          </Typography>
        </Box>
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <MenuItem 
            component={Link}
            to="/master/users"
            sx={{ 
              color: '#41ab5d',
              '&:hover': { backgroundColor: '#f0f7ff' }
            }}
          >
            <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            User Management
          </MenuItem>
        )}
        <MenuItem 
          sx={{ 
            borderTop: '1px solid #f0f0f0',
            color: '#ff4d4f',
            mt: 1,
            '&:hover': { backgroundColor: '#fff1f0' }
          }}
          onClick={handleLogout}
        >
          <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default Layout;